import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, Instagram, Facebook } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\S+@\S+\.\S+$/.test(form.email) || !form.message.trim()) {
      toast.error("Please fill in your name, valid email, and a message.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll be in touch.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (e2) {
      toast.error("Couldn't send. Please call us instead.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0A0A0A] py-24 md:py-32 noise-overlay"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="red-bar" />
              <span className="font-mono-accent text-xs text-zinc-400">06 — GET IN TOUCH</span>
            </div>
            <h2 className="font-display font-bold uppercase text-white text-5xl md:text-7xl leading-[0.9] tracking-tighter mb-10">
              Questions? <br />
              <span className="text-[#DC2626]">Call us.</span>
            </h2>
            <div className="space-y-6">
              <ContactRow icon={Phone} label="Phone" value={BRAND.phone} href={`tel:${BRAND.phoneRaw}`} testid="contact-phone" />
              <ContactRow icon={Mail} label="Email" value={BRAND.email} href={`mailto:${BRAND.email}`} testid="contact-email" />
              <ContactRow icon={MapPin} label="Service Area" value={BRAND.area} testid="contact-area" />
              <ContactRow icon={Clock} label="Hours" value={BRAND.hours} testid="contact-hours" />
            </div>

            <div className="mt-12 flex items-center gap-4">
              <a
                href={BRAND.socials.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="social-instagram"
                className="w-12 h-12 border border-[#27272A] hover:border-[#DC2626] hover:text-[#DC2626] text-zinc-400 flex items-center justify-center transition-colors"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href={BRAND.socials.facebook}
                target="_blank"
                rel="noreferrer"
                data-testid="social-facebook"
                className="w-12 h-12 border border-[#27272A] hover:border-[#DC2626] hover:text-[#DC2626] text-zinc-400 flex items-center justify-center transition-colors"
              >
                <Facebook className="h-5 w-5" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-6">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="bg-[#121212] border border-[#27272A] p-8 md:p-10 space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <CField label="Name">
                  <input
                    data-testid="contact-name"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    className={contactInput}
                    placeholder="Your name"
                  />
                </CField>
                <CField label="Email">
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    className={contactInput}
                    placeholder="you@example.com"
                  />
                </CField>
              </div>
              <CField label="Phone (optional)">
                <input
                  data-testid="contact-phone-input"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className={contactInput}
                  placeholder="0400 000 000"
                />
              </CField>
              <CField label="Message">
                <textarea
                  data-testid="contact-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  className={`${contactInput} resize-none`}
                  placeholder="Tell us about your car and what you're after."
                />
              </CField>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-3 bg-[#DC2626] hover:bg-[#EF4444] text-white font-display font-bold uppercase tracking-wider text-sm py-4 transition-colors disabled:opacity-60"
              >
                <Send className="h-4 w-4" />
                {busy ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const ContactRow = ({ icon: Icon, label, value, href, testid }) => {
  const Body = (
    <div className="flex items-start gap-5">
      <div className="w-12 h-12 border border-[#27272A] flex items-center justify-center text-[#DC2626] flex-shrink-0">
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="font-mono-accent text-[10px] text-zinc-500 mb-1">{label}</div>
        <div className="text-white text-lg">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testid} className="block hover:opacity-80 transition-opacity">
      {Body}
    </a>
  ) : (
    <div data-testid={testid}>{Body}</div>
  );
};

const contactInput =
  "w-full bg-transparent border border-[#27272A] focus:border-[#DC2626] text-white py-3 px-4 placeholder-zinc-600 focus:outline-none focus:ring-0 transition-colors";

const CField = ({ label, children }) => (
  <div>
    <label className="font-mono-accent text-[10px] text-zinc-500 mb-2 block">{label}</label>
    {children}
  </div>
);
